/**
 * Size chart video popup — modal, model thumbnails, URL query open, PDP floating trigger.
 * Config: <script type="application/json" data-size-chart-config> per section instance.
 */
(function () {
  const POPUP_CONFIG = {
    desktop: {
      width: { md: 440, lg: 480, xl: 520 },
      height: { md: 800, lg: 900, xl: 950 },
    },
    mobileBreakpoint: 768,
    maxHeightOffset: 48,
  };

  const state = {
    controllers: [],
    globalClickBound: false,
    globalModalBound: false,
    visualViewportListenerAttached: false,
  };

  /** Preserved scroll position when locking body (iOS / mobile Chrome). Null = not locked. */
  let bodyScrollLockY = null;

  function slugify(s) {
    return String(s || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  function getVisibleViewportSize() {
    const vv = window.visualViewport;
    if (vv) {
      return { width: vv.width, height: vv.height };
    }
    return { width: window.innerWidth, height: window.innerHeight };
  }

  function lockBodyScroll() {
    if (bodyScrollLockY !== null) {
      return;
    }
    bodyScrollLockY = window.scrollY || document.documentElement.scrollTop || 0;
    const body = document.body;
    const html = document.documentElement;
    body.style.setProperty("position", "fixed");
    body.style.setProperty("top", `-${bodyScrollLockY}px`);
    body.style.setProperty("left", "0");
    body.style.setProperty("right", "0");
    body.style.setProperty("width", "100%");
    body.style.setProperty("overflow", "hidden");
    html.style.setProperty("overflow", "hidden");
  }

  function unlockBodyScroll() {
    if (bodyScrollLockY === null) {
      return;
    }
    const y = bodyScrollLockY;
    bodyScrollLockY = null;
    const body = document.body;
    const html = document.documentElement;
    body.style.removeProperty("position");
    body.style.removeProperty("top");
    body.style.removeProperty("left");
    body.style.removeProperty("right");
    body.style.removeProperty("width");
    body.style.removeProperty("overflow");
    html.style.removeProperty("overflow");
    window.scrollTo(0, y);
  }

  function parseConfigs() {
    const tags = document.querySelectorAll(
      'script[type="application/json"][data-size-chart-config]',
    );
    const list = [];
    tags.forEach((tag) => {
      try {
        list.push(JSON.parse(tag.textContent));
      } catch (e) {
        console.warn("size-chart-video-popup: invalid JSON config", e);
      }
    });
    return list;
  }

  function createController(config) {
    const sectionId = String(config.sectionId);
    const prefix = `size-chart-${sectionId}`;
    const overlay = document.getElementById(`${prefix}-overlay`);
    if (!overlay) return null;

    const dialog = document.getElementById(`${prefix}-dialog`);
    const mainVideo = document.getElementById(`${prefix}-video`);
    const videoSource = document.getElementById(`${prefix}-source`);
    const childNameEl = document.getElementById(`${prefix}-child-name`);
    const titleSubtextEl = document.getElementById(`${prefix}-subtext`);
    const thumbnailContainer = document.getElementById(`${prefix}-thumbs`);
    const closeBtn = document.getElementById(`${prefix}-close`);

    const VIDEO_DATA = Array.isArray(config.videos) ? config.videos : [];
    let isOpen = false;
    let activeVideoIndex = 0;

    function updatePopupDimensions() {
      if (!dialog) return;
      const width = window.innerWidth;
      const visible = getVisibleViewportSize();
      let popupWidth;
      let popupHeight;

      if (width >= 1280) {
        popupWidth = `${POPUP_CONFIG.desktop.width.xl}px`;
        popupHeight = `${POPUP_CONFIG.desktop.height.xl}px`;
      } else if (width >= 1024) {
        popupWidth = `${POPUP_CONFIG.desktop.width.lg}px`;
        popupHeight = `${POPUP_CONFIG.desktop.height.lg}px`;
      } else if (width >= POPUP_CONFIG.mobileBreakpoint) {
        popupWidth = `${POPUP_CONFIG.desktop.width.md}px`;
        popupHeight = `${POPUP_CONFIG.desktop.height.md}px`;
      } else {
        const vh = Math.max(0, Math.round(visible.height));
        popupWidth = "100%";
        popupHeight = `${vh}px`;
        dialog.style.maxHeight = `${vh}px`;
        dialog.style.width = popupWidth;
        dialog.style.height = popupHeight;
        return;
      }

      dialog.style.width = popupWidth;
      dialog.style.height = popupHeight;
      dialog.style.maxHeight = `calc(100vh - ${POPUP_CONFIG.maxHeightOffset}px)`;
    }

    function updateVideoContent() {
      if (!VIDEO_DATA.length || !videoSource || !mainVideo) return;
      const current = VIDEO_DATA[activeVideoIndex];
      if (!current) return;
      videoSource.src = current.videoUrl || "";
      if (childNameEl) childNameEl.textContent = current.childName || "";
      if (titleSubtextEl) titleSubtextEl.textContent = current.titleSubtext || "";
      mainVideo.load();
      mainVideo.play().catch(() => {});
    }

    function updateThumbnailStates() {
      if (!thumbnailContainer) return;
      const buttons = thumbnailContainer.querySelectorAll(
        ".wt-size-chart-popup__thumb-btn",
      );
      buttons.forEach((button, index) => {
        button.classList.toggle("is-active", index === activeVideoIndex);
      });
    }

    function switchVideo(index) {
      activeVideoIndex = index;
      updateVideoContent();
      updateThumbnailStates();
    }

    function generateThumbnails() {
      if (!thumbnailContainer) return;
      thumbnailContainer.innerHTML = "";

      VIDEO_DATA.forEach((video, index) => {
        const col = document.createElement("div");
        col.className = "wt-size-chart-popup__thumb-col";

        const button = document.createElement("button");
        button.type = "button";
        button.className = "wt-size-chart-popup__thumb-btn";
        button.addEventListener("click", () => switchVideo(index));

        const img = document.createElement("img");
        if (video.avatarUrl) {
          img.src = video.avatarUrl;
        }
        img.alt = `${video.childName || "Model"} avatar`;

        button.appendChild(img);

        const label = document.createElement("span");
        label.className = "wt-size-chart-popup__thumb-label";
        label.textContent = video.circleSubtext || "";

        col.appendChild(button);
        col.appendChild(label);
        thumbnailContainer.appendChild(col);
      });

      updateThumbnailStates();
    }

    function syncModalBodyClass() {
      const anyOpen = state.controllers.some((c) => {
        const el = document.getElementById(`size-chart-${c.sectionId}-overlay`);
        return el?.classList.contains("is-open");
      });
      document.body.classList.toggle("wt-size-chart-popup-open", anyOpen);
    }

    function openPopup() {
      if (!VIDEO_DATA.length) return;
      isOpen = true;
      overlay.classList.add("is-open");
      lockBodyScroll();
      syncModalBodyClass();
      updatePopupDimensions();
      updateVideoContent();
      generateThumbnails();
    }

    function closePopup() {
      isOpen = false;
      overlay.classList.remove("is-open");
      unlockBodyScroll();
      syncModalBodyClass();
      if (dialog) {
        dialog.style.removeProperty("max-height");
        dialog.style.removeProperty("height");
        dialog.style.removeProperty("width");
      }
      if (mainVideo) {
        mainVideo.pause();
        try {
          mainVideo.currentTime = 0;
        } catch (e) {
          /* ignore */
        }
      }
    }

    function findVideoIndexFromParam(val) {
      if (val == null) return -1;
      const v = String(val).trim();

      if (/^\d+$/.test(v)) {
        const n = parseInt(v, 10);
        if (n >= 1 && n <= VIDEO_DATA.length) return n - 1;
      }

      const byId = VIDEO_DATA.findIndex((x) => String(x.id) === v);
      if (byId !== -1) return byId;

      const slug = slugify(v);
      const byName = VIDEO_DATA.findIndex(
        (x) => slugify(x.childName) === slug,
      );
      if (byName !== -1) return byName;

      return VIDEO_DATA.findIndex((x) =>
        String(x.videoUrl || "").includes(v),
      );
    }

    function shouldOpenFromQuery(params) {
      return (
        params.has("videoModal") ||
        params.get("modal") === "size-chart" ||
        params.get("open") === "video" ||
        params.has("video")
      );
    }

    function checkQueryParamsAndOpen() {
      const inEditor = window.Shopify && window.Shopify.designMode;
      if (inEditor) return;
      if (!VIDEO_DATA.length) return;

      const params = new URLSearchParams(window.location.search);
      if (!shouldOpenFromQuery(params)) return;

      const videoParam = params.get("video");
      const idx = findVideoIndexFromParam(videoParam);
      if (idx > -1) activeVideoIndex = idx;
      openPopup();
    }

    closeBtn?.addEventListener("click", closePopup);

    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) closePopup();
    });

    return {
      config,
      openPopup,
      closePopup,
      checkQueryParamsAndOpen,
      updatePopupDimensions,
      get triggerClass() {
        return (config.triggerClass || "size-chart-video-modal").trim();
      },
      get sectionId() {
        return sectionId;
      },
      hasVideos: VIDEO_DATA.length > 0,
    };
  }

  function slideHasTrigger(slide, className) {
    const first = className.split(/\s+/).filter(Boolean)[0];
    if (!first) return false;
    return slide.querySelector(`.${first}`) != null;
  }

  function clearProductGalleryFloatingTriggers() {
    const gallery = document.querySelector(
      "gallery-section[data-product-page].wt-product__gallery",
    );
    if (!gallery) return;
    gallery
      .querySelectorAll(".wt-size-chart-popup__trigger")
      .forEach((node) => node.remove());
    gallery
      .querySelectorAll(".wt-size-chart-popup__slide--anchor")
      .forEach((el) => el.classList.remove("wt-size-chart-popup__slide--anchor"));
  }

  function injectFloatingTrigger(controller, playIconUrl, triggerLabel) {
    if (!controller.hasVideos) return;

    const gallery = document.querySelector(
      "gallery-section[data-product-page].wt-product__gallery",
    );
    if (!gallery) return;

    clearProductGalleryFloatingTriggers();

    const slide = gallery.querySelector("li.swiper-slide");
    if (!slide) return;

    const triggerClass = controller.triggerClass;
    if (slideHasTrigger(slide, triggerClass)) return;

    slide.classList.add("wt-size-chart-popup__slide--anchor");

    const cfg = controller.config || {};
    const iconRight = String(cfg.playIconPosition || "left").toLowerCase() === "right";

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = `wt-size-chart-popup__trigger ${triggerClass}`;
    btn.dataset.sizeChartSectionId = controller.sectionId;
    btn.dataset.scTrigger = String(controller.sectionId);

    const label = document.createTextNode(triggerLabel || "See the size guide");
    let img = null;
    if (playIconUrl) {
      img = document.createElement("img");
      img.src = playIconUrl;
      img.alt = "";
    }

    if (iconRight) {
      btn.appendChild(label);
      if (img) btn.appendChild(img);
    } else {
      if (img) btn.appendChild(img);
      btn.appendChild(label);
    }

    slide.appendChild(btn);
  }

  function reinjectProductGalleryFloatingTrigger() {
    const primary =
      state.controllers.find((c) => c.hasVideos) || state.controllers[0];
    const playIconDefault =
      "https://cdn.shopify.com/s/files/1/0251/0749/6994/files/play-button-white.svg?v=1755535746";
    const playIcon =
      primary?.config?.playIconUrl &&
      String(primary.config.playIconUrl).trim() !== ""
        ? primary.config.playIconUrl
        : playIconDefault;
    const triggerLabel = primary?.config?.triggerLabel || "See the size guide";
    if (primary && primary.hasVideos) {
      injectFloatingTrigger(primary, playIcon, triggerLabel);
    }
  }

  function bindGlobalModalOnce() {
    if (state.globalModalBound) return;
    state.globalModalBound = true;

    document.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;
      state.controllers.forEach((c) => {
        const el = document.getElementById(`size-chart-${c.sectionId}-overlay`);
        if (el?.classList.contains("is-open")) c.closePopup();
      });
    });

    const syncOpenModals = () => {
      state.controllers.forEach((c) => {
        const el = document.getElementById(`size-chart-${c.sectionId}-overlay`);
        if (el?.classList.contains("is-open")) c.updatePopupDimensions();
      });
    };

    window.addEventListener("resize", syncOpenModals);

    if (!state.visualViewportListenerAttached && window.visualViewport) {
      state.visualViewportListenerAttached = true;
      window.visualViewport.addEventListener("resize", syncOpenModals);
      window.visualViewport.addEventListener("scroll", syncOpenModals);
    }
  }

  function bindGlobalClickOnce() {
    if (state.globalClickBound) return;
    state.globalClickBound = true;

    document.addEventListener("click", (event) => {
      const ctrls = state.controllers;

      const injected = event.target.closest(".wt-size-chart-popup__trigger");
      if (injected) {
        const sid = injected.dataset.sizeChartSectionId;
        const ctrl = ctrls.find((c) => c.sectionId === sid);
        if (ctrl) {
          event.preventDefault();
          ctrl.openPopup();
        }
        return;
      }

      let manual = null;
      for (let i = 0; i < ctrls.length; i++) {
        const parts = ctrls[i].triggerClass.split(/\s+/).filter(Boolean);
        if (!parts.length) continue;
        const sel = "." + parts.join(".");
        manual = event.target.closest(sel);
        if (manual) break;
      }
      if (!manual) return;
      if (manual.classList.contains("wt-size-chart-popup__trigger")) return;

      const sid = manual.dataset?.sizeChartSectionId;
      const primary = ctrls.find((c) => c.hasVideos);
      const ctrl = ctrls.find((c) => c.sectionId === sid) || primary;
      if (ctrl && ctrl.hasVideos) {
        event.preventDefault();
        ctrl.openPopup();
      }
    });
  }

  function runInit() {
    const configs = parseConfigs();
    state.controllers = configs
      .map((cfg) => createController(cfg))
      .filter(Boolean);

    bindGlobalClickOnce();
    bindGlobalModalOnce();

    reinjectProductGalleryFloatingTrigger();

    document.removeEventListener("gallery:updated", onGalleryUpdated);
    document.addEventListener("gallery:updated", onGalleryUpdated);

    const runQuery = () => {
      state.controllers.forEach((c) => c.checkQueryParamsAndOpen());
    };

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", runQuery, { once: true });
    } else {
      runQuery();
    }
  }

  function onGalleryUpdated(e) {
    if (e.detail?.selector !== ".wt-product__gallery") return;
    requestAnimationFrame(() => reinjectProductGalleryFloatingTrigger());
  }

  runInit();

  document.addEventListener("shopify:section:load", (e) => {
    const target = e.target;
    if (
      target?.classList?.contains?.("wt-size-chart-video-popup-section") ||
      target?.querySelector?.("[data-size-chart-config]")
    ) {
      runInit();
    }
  });
})();
